import { Injectable } from '@angular/core';

import { environment } from '@environments/environment';

import Resources from '@constants/resourses';

import { TealiumUtagService } from './utag.service';
import { SessionService } from './session.service';
import { SocketService } from './socket.service';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  configuration: any = {};

  // Anchor a relative path to the app's HTTPS origin so a redirect can't inherit
  // a non-HTTPS protocol if this page was somehow reached over HTTP.
  private toAbsoluteUrl(path: string): string {
    return new URL(path, environment.home_url).toString();
  }

  constructor(
    private tealium: TealiumUtagService,
    private sessionService: SessionService,
    private socketService: SocketService,
    private utilityService: UtilityService
  ) {
    this.tealium.setConfig({
      account: environment.tealium_account,
      profile: environment.tealium_profile,
      environment: environment.tealium_environment
    });
  }

  // Initialy load - now returns Promise to wait for session data
  loadConfig(): Promise<void> {
    return new Promise((resolve) => {
      // Initialise presence socket after session is restored
      const identifier = this.sessionService.getSessionId();

      // Connect to presence socket if enabled
      if (environment.socketPresence?.enabled) {
        const userName = this.sessionService.getData(Resources.SessionStorageKeys.PersonalDetails)?.firstName;
        this.socketService.connectPresence(identifier, userName);
      }

      // Check you are not in the landing page, the login flow (AuthenticateComponent owns its own correlationId handling there), or a standalone informational page that doesn't need a correlationId
      if (window.location.pathname !== '/' && window.location.pathname !== '/home' && window.location.pathname !== '/home-new' && window.location.pathname !== '/frequently-ask-question' && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/pdf')) {
        const correlationId = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId);
        const urlCorrelationId = new URLSearchParams(window.location.search).get('correlationId');

        // Scenario 1: correlationId present in both the URL and the session, but they don't match -> back to postcode
        if (!!urlCorrelationId && !!correlationId && urlCorrelationId !== correlationId) {
          this.sessionService.clearData();
          this.sessionService.clearSessionStorage();
          this.sessionService.clearLocalStorage();
          window.location.href = this.toAbsoluteUrl('/');
          return;
        }

        // Scenario 2: correlationId present in the URL only, no session for it yet -> must log in to access it
        if (!!urlCorrelationId && !correlationId) {
          window.location.href = this.toAbsoluteUrl(`/login?correlationId=${urlCorrelationId}`);
          return;
        }

        // Scenario 3: correlationId present in the session (URL absent, or present and matching) -> validate its state
        const activeCorrelationId = correlationId || urlCorrelationId;
        if (activeCorrelationId) {
          this.sessionService.restoreSessionData(activeCorrelationId).subscribe({
            next: (response) => {
              // Not logged in -> back to postcode. Logged in -> stay on the current page.
              if (!response?.user_loggedin && !this.sessionService.getItemFromSession(Resources.SessionStorageKeys.IsRetry)) {
                window.location.href = this.toAbsoluteUrl('/');
                return;
              }

              this.setConfiguration();
              resolve();
            }, error: (error) => {
              // Handle error during session restoration
              console.error('Error restoring session data:', error);
              window.location.href = this.toAbsoluteUrl('/');
            }
          });
        } else {
          // No correlationId anywhere -> nothing to restore, back to postcode
          this.sessionService.clearData();
          this.sessionService.clearSessionStorage();
          this.sessionService.clearLocalStorage();
          window.location.href = this.toAbsoluteUrl('/');
          this.setConfiguration();
          resolve();
        }
      } else {
        this.setConfiguration();
        resolve();
      }
    });
  }

  // Set configuration [NOT IN USE]
  private setConfiguration(): void {
    this.configuration = {
      consentConfig: {
        email: {
          required: false
        },
        consent: {
          required: false
        }
      }
    };
  }

  // Get config [NOT IN USE]
  getConfig(): any {
    return this.configuration;
  }
}
